"use client";

import { type KeyboardEvent } from "react";
import { Download, LoaderCircle } from "lucide-react";
import { classNames } from "../ui/class-names";
import type { DownloadState } from "./types";

export function FilenameDownloadButton({
  filename,
  onFilenameChange,
  canDownload,
  downloadState,
  onDownload,
  compact = false,
  hideFilename = false,
}: Readonly<{
  filename: string;
  onFilenameChange: (filename: string) => void;
  canDownload: boolean;
  downloadState: DownloadState;
  onDownload: () => void;
  compact?: boolean;
  hideFilename?: boolean;
}>) {
  const isGenerating = downloadState.status === "generating";
  const isCooldown = downloadState.status === "cooldown";
  const isDisabled = !canDownload || isGenerating || isCooldown;
  const downloadLabel = isGenerating
    ? "Preparing PDF"
    : isCooldown
      ? `Wait ${downloadState.remainingSeconds}s`
      : "Download PDF";
  const showFilename = !hideFilename;
  const showLabel = !(compact || hideFilename) || isCooldown;
  const buttonTitle = isCooldown
    ? `Wait ${downloadState.remainingSeconds}s before downloading again`
    : canDownload
      ? "Download PDF"
      : "Add markdown to enable PDF download";
  // Match editorToolbarMediumThreshold (900). Applied before JS measures so
  // SSR never flashes a full-width download control into an icon button.
  const narrowContainer = "@max-[900px]/toolbar:hidden";
  const narrowIconButton =
    "@max-[900px]/toolbar:size-8 @max-[900px]/toolbar:rounded-md @max-[900px]/toolbar:border @max-[900px]/toolbar:px-0";

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      event.preventDefault();
      onDownload();
    }
  }

  return (
    <div
      className={classNames(
        "inline-flex items-stretch transition",
        showFilename
          ? classNames(
              "rounded-md shadow-sm focus-within:ring-2 focus-within:ring-brand/20 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-brand-hover",
              "@max-[900px]/toolbar:shadow-none @max-[900px]/toolbar:focus-within:outline-none @max-[900px]/toolbar:focus-within:ring-0",
            )
          : undefined,
      )}
      title={
        canDownload
          ? showFilename
            ? "Edit filename, then click to download PDF"
            : "Download PDF"
          : "Add markdown to enable PDF download"
      }
    >
      {showFilename ? (
        <input
          aria-label="Filename"
          autoComplete="off"
          maxLength={140}
          placeholder="File name..."
          value={filename}
          onChange={(event) => onFilenameChange(event.target.value)}
          onKeyDown={handleKeyDown}
          className={classNames(
            "h-10 min-w-0 flex-1 truncate rounded-l-md rounded-r-none border-y border-l border-r-0 border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-950 outline-none transition placeholder:text-zinc-400 hover:bg-zinc-100 focus:bg-white",
            compact ? "w-28" : "w-44 sm:w-56",
            narrowContainer,
          )}
        />
      ) : null}
      <button
        type="button"
        aria-label={downloadLabel}
        aria-busy={isGenerating ? "true" : undefined}
        disabled={isDisabled}
        title={buttonTitle}
        onClick={onDownload}
        className={classNames(
          "inline-flex shrink-0 items-center justify-center gap-2 text-sm font-semibold shadow-sm transition",
          showFilename
            ? classNames(
                "h-10 rounded-r-md rounded-l-none border-y border-r border-l-0 px-3",
                narrowIconButton,
              )
            : compact || hideFilename
              ? isCooldown
                ? "h-8 min-w-8 rounded-md border px-2"
                : "size-8 rounded-md border"
              : classNames("h-10 rounded-md border px-3", narrowIconButton),
          isDisabled
            ? "cursor-not-allowed border-zinc-200 bg-zinc-200 text-zinc-500"
            : "border-brand bg-brand text-white hover:bg-brand-hover",
          isGenerating ? "cursor-wait bg-brand-hover text-white" : undefined,
        )}
      >
        {isGenerating ? (
          <LoaderCircle aria-hidden="true" className="size-4 animate-spin" />
        ) : (
          <Download aria-hidden="true" className="size-4" />
        )}
        {showLabel ? (
          <span className={isCooldown ? undefined : narrowContainer}>
            {downloadLabel}
          </span>
        ) : null}
      </button>
    </div>
  );
}
